import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import Parser from 'html-react-parser';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import Input from '../common/input';
import ButtonCustom from '../common/__deprecated__/button-custom';
import { commons as validations } from '../../utils/validations';
import { Routes } from '../../config/routes';
import { operatingCountriesForAutocomplete } from '../../utils/common-utils';
import InputCheckbox from '../common/input-checkbox';
import { TextField, MenuItem } from '@mui/material';
import { Autocomplete } from '../common/autocomplete';
import l from '../../lang';
import './styles.scss';
import MessageError from '../common/message-error';
import { getLanguage, scrollIntoView } from '../../utils';
import ButtonLink from '../common/button-link';
import TooltipIcon from '../common/tooltip-icon';
import Dialog from '../common/dialog';
import PrivacyPolicies from '../common/privacy-policies';
import TermsAndConditions from '../common/terms-and-conditions';
import LegalDocumentViewer, { LEGAL_DOCUMENT_DIALOG_PAPER_CLASS } from '../common/legal-document-viewer';
import { segmentSignUpPrivacyPolicy, segmentSignUpTc } from '../../utils/analytics';
import { getTask } from '../../services';
import { Urls } from '../../config/urls';
import { MessageTypes } from '../../constants';

const KEY_RECAPTCHA = import.meta.env.REACT_APP_RECAPTCHA_SITE_KEY_DLGO;
const ACTION = 'signup';

// Shown when reCAPTCHA cannot produce a token (e.g. api.js failed to load).
// Reuses MessageError's errorInfo shape ({ level, title, message }).
const RECAPTCHA_ERROR_INFO = {
  level: MessageTypes.ERROR,
  title: 'signup.recaptcha.error.title',
  message: 'signup.recaptcha.error.message',
};

/**
 * Safely obtains a reCAPTCHA v3 token.
 *
 * Guards against the "Invalid site key or not loaded in api.js" error seen in
 * production (mostly bot traffic on /signup) by:
 *  - verifying the site key is configured
 *  - waiting for grecaptcha to be fully loaded via grecaptcha.ready(...)
 *  - verifying grecaptcha.execute exists before calling it
 *  - never letting the error propagate as an unhandled rejection
 *
 * Resolves with the token on success, or null when reCAPTCHA is unavailable or fails.
 */
const executeReCaptcha = () =>
  new Promise(resolve => {
    if (!KEY_RECAPTCHA) {
      resolve(null);
      return;
    }

    const grecaptcha = window.grecaptcha;

    if (!grecaptcha || typeof grecaptcha.execute !== 'function') {
      resolve(null);
      return;
    }

    const runExecute = () => {
      try {
        grecaptcha
          .execute(KEY_RECAPTCHA, { action: ACTION })
          .then(token => resolve(token || null))
          .catch(() => resolve(null));
      } catch {
        resolve(null);
      }
    };

    // grecaptcha.ready guarantees api.js finished loading before execute() runs.
    if (typeof grecaptcha.ready === 'function') {
      grecaptcha.ready(runExecute);
    } else {
      runExecute();
    }
  });

const hasRequiredLegalDocumentVersions = documents => {
  const termsVersionId = documents.find(document => document.documentType === 'TERMS_AND_CONDITIONS')?.versionId;
  const privacyVersionId = documents.find(document => document.documentType === 'PRIVACY_POLICY')?.versionId;

  return Boolean(termsVersionId && privacyVersionId);
};

const getLegalDocumentVersionIds = documents => ({
  termsAndConditionsVersionId:
    documents.find(document => document.documentType === 'TERMS_AND_CONDITIONS')?.versionId ?? null,
  privacyPolicyVersionId:
    documents.find(document => document.documentType === 'PRIVACY_POLICY')?.versionId ?? null,
});

const initialValues = {
  name: '',
  email: '',
  password: '',
  country: '',
  company: '',
  functionalCurrency: '',
  marketingConsent: true,
  termsAndConditions: false,
};

const SignUpForm = ({ loading, error, errorInfo, onSubmit, isSandbox }) => {
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const functionalCurrencyRef = useRef(null);
  const marketingConsentRef = useRef(null);
  const companyTypeRef = useRef(null);
  const termsAndConditionsRef = useRef(null);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showPrivacyPolicies, setShowPrivacyPolicies] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [legalDocumentsLoading, setLegalDocumentsLoading] = useState(false);
  const [registrationConfig, setRegistrationConfig] = useState({ allowsPhysical: true, allowsCompany: true });

  const history = useHistory();
  const language = getLanguage();

  const [showError, setShowError] = useState(true);
  const [recaptchaError, setRecaptchaError] = useState(false);
  useEffect(() => {
    if (KEY_RECAPTCHA) {
      loadReCaptcha(KEY_RECAPTCHA);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchLegalDocuments = async () => {
      if (!selectedCountry) {
        setLegalDocuments([]);
        setLegalDocumentsLoading(false);
        return;
      }

      setLegalDocumentsLoading(true);
      setLegalDocuments([]);

      const params = new URLSearchParams();
      params.set('language', language);
      params.set('country', selectedCountry);

      const { response } = await getTask({
        url: `${Urls.api.currentLegalDocuments}?${params.toString()}`,
      });

      if (!cancelled) {
        setLegalDocuments(response || []);
        setLegalDocumentsLoading(false);
      }
    };

    fetchLegalDocuments();

    return () => {
      cancelled = true;
    };
  }, [language, selectedCountry]);

  useEffect(() => {
    let cancelled = false;

    const fetchRegistrationConfig = async () => {
      if (!selectedCountry) {
        setRegistrationConfig({ allowsPhysical: true, allowsCompany: true });
        return;
      }

      const { response } = await getTask({
        url: `${Urls.api.countriesRegistrationConfig}?country=${selectedCountry}`,
      });

      if (!cancelled) {
        const config = Array.isArray(response)
          ? response.find(item => item.country === selectedCountry)
          : null;
        setRegistrationConfig({
          allowsPhysical: config ? config.allowsPhysical : true,
          allowsCompany: config ? config.allowsCompany : true,
        });
      }
    };

    fetchRegistrationConfig();

    return () => {
      cancelled = true;
    };
  }, [selectedCountry]);

  const validate = values => {
    const errors = {};
    if (!values.email) {
      errors.email = l('form.input.required');
    } else if (!validations.emailValid(values.email)) {
      errors.email = l('form.email.invalid');
    }
    if (!values.password) {
      errors.password = l('form.input.required');
    } else if (!validations.passwordRequiredCharacters(values.password)) {
      errors.password = l('form.input.password.invalid');
    } else if (!validations.passwordLengthRequired(values.password)) {
      errors.password = l('form.input.password.invalidLength');
    }
    if (!values.name) {
      errors.name = l('form.input.required');
    } else if (!validations.nameValid(values.name)) {
      errors.name = l('form.input.name.invalid');
    }
    if (!values.country) {
      errors.country = l('form.input.required');
    }
    if (!values.termsAndConditions) {
      errors.termsAndConditions = l('form.input.termsAndConditions.invalid');
    }
    if (values.company === '') {
      errors.company = l('form.input.required');
    }
    return errors;
  };

  const onClickTermsConditionsHandler = country => {
    if (!country) {
      return;
    }
    segmentSignUpTc();
    setShowTermsAndConditions(true);
  };

  const onClickPrivacyPoliciesHandler = country => {
    if (!country) {
      return;
    }
    segmentSignUpPrivacyPolicy();
    setShowPrivacyPolicies(true);
  };

  return (
    <div className="sign-up-form">
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={(values, { setSubmitting }) => {
          setShowError(true);
          setRecaptchaError(false);
          setSubmitting(true);

          if (!hasRequiredLegalDocumentVersions(legalDocuments)) {
            setSubmitting(false);
            return;
          }

          executeReCaptcha()
            .then(token => {
              // reCAPTCHA could not produce a token (api.js not loaded, invalid site key).
              // Don't hit the backend with a request it will reject; show a clear message instead.
              if (!token) {
                setRecaptchaError(true);
                return;
              }

              const { termsAndConditionsVersionId, privacyPolicyVersionId } = getLegalDocumentVersionIds(legalDocuments);

              onSubmit({
                name: values.name,
                email: values.email,
                password: values.password,
                country: values.country,
                marketingConsent: values.marketingConsent,
                termsAndConditions: values.termsAndConditions,
                termsAndConditionsVersionId,
                privacyPolicyVersionId,
                company: values.company,
                recaptcha: token,
              });
            })
            .finally(() => setSubmitting(false));
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => {
          const hasValidationErrors = !_.isEmpty(validate(values));
          const legalDocumentsReady = Boolean(values.country) && hasRequiredLegalDocumentVersions(legalDocuments);
          const canAcceptLegalDocuments = legalDocumentsReady && !legalDocumentsLoading;

          return (
            <form onSubmit={handleSubmit}>
              <h1 className="signup-title">{l(isSandbox ? 'signup.sandbox.dlocalCreateAccount' : 'signup.dlocalCreateAccount')}</h1>
              <div ref={emailRef}>
                <Input
                  type="email"
                  name="email"
                  label={l('emailAddress.label')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  maxLength={100}
                  disabled={loading}
                  error={touched.email && errors.email}
                />
              </div>
              <div ref={nameRef}>
                <Input
                  type="name"
                  name="name"
                  label={l('name.label')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  maxLength={100}
                  disabled={loading}
                  error={touched.name && errors.name}
                  message={l('name.helpText')}
                />
              </div>
              <div ref={functionalCurrencyRef} className="country-input">
                <Autocomplete
                  label={l('businessCountry.label')}
                  value={values.country}
                  onChange={({ target: { value } }) => {
                    const country = value ?? '';
                    // Disable by default "marketingConsent" for these countries.
                    if (['BR', 'ES', 'US'].includes(country)) {
                      setFieldValue('marketingConsent', false);
                    }
                    setFieldValue('termsAndConditions', false);
                    setLegalDocuments([]);
                    setLegalDocumentsLoading(Boolean(country));
                    setSelectedCountry(country);
                    setFieldValue('company', '');
                    setFieldValue('country', country);
                  }}
                  options={operatingCountriesForAutocomplete()}
                  disabled={loading}
                  error={touched.country && Boolean(errors.country)}
                  helperText={touched.country && errors.country}
                />
                <div className="country-popup">
                  <TooltipIcon text={Parser(l('businessCountry.tooltip'))} icon={<InfoIcon color="primary" />} />
                </div>
              </div>
              <div ref={companyTypeRef} className="country-input">
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  id="id-select-company"
                  name="company"
                  label={l('companyType.label')}
                  value={values.company}
                  onChange={e => setFieldValue('company', e.target.value)}
                  disabled={loading}
                  error={Boolean(touched.company && errors.company)}
                  helperText={touched.company && errors.company}
                  sx={{
                    marginTop: 2,
                    '& .MuiInputLabel-root': { fontSize: '14px', fontWeight: 400 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff9e',
                      fontSize: '14px',
                      fontWeight: 400,
                    },
                    '& .MuiOutlinedInput-input': { fontSize: '14px', fontWeight: 400 },
                    '& .MuiSelect-select': {
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {registrationConfig.allowsCompany && (
                    <MenuItem value>{l('companyType.company')}</MenuItem>
                  )}
                  {registrationConfig.allowsPhysical && (
                    <MenuItem value={false}>{l('companyType.physical')}</MenuItem>
                  )}
                </TextField>
                <div className="country-popup">
                  <TooltipIcon text={Parser(l('companyType.tooltip'))} icon={<InfoIcon color="primary" />} />
                </div>
              </div>
              <div ref={passwordRef}>
                <Input
                  type="password"
                  name="password"
                  label={l('password.label')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  maxLength={50}
                  disabled={loading}
                  error={touched.password && errors.password}
                  message={l('password.helpText')}
                />
              </div>
              <div className="marketing-consent" ref={marketingConsentRef}>
                <div className="marketing-consent-container">
                  <InputCheckbox
                    type="checkbox"
                    name="marketingConsent"
                    label={l('marketingConsent.label')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.marketingConsent}
                    disabled={loading || !values.country}
                  />
                </div>
                <div className="marketing-consent-container" ref={termsAndConditionsRef}>
                  <InputCheckbox
                    type="checkbox"
                    name="termsAndConditions"
                    label={
                      <span>
                        <span>{l('signUp.termsAndConditions.label')} </span>
                        <ButtonLink
                          text={l('signUp.termsAndConditions')}
                          disabled={!canAcceptLegalDocuments}
                          onClick={() => onClickTermsConditionsHandler(values.country)}
                        />
                        <span> {l('signUp.policy.label')} </span>
                        <ButtonLink
                          text={l('signUp.policy')}
                          disabled={!canAcceptLegalDocuments}
                          onClick={() => onClickPrivacyPoliciesHandler(values.country)}
                        />
                      </span>
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.termsAndConditions}
                    disabled={loading || !canAcceptLegalDocuments}
                    error={touched.termsAndConditions && errors.termsAndConditions}
                  />
                </div>
              </div>
              <div>
                <div className="sign-up-error">
                  {error && showError && <MessageError errorInfo={errorInfo} onClose={() => setShowError(false)} />}
                  {recaptchaError && (
                    <MessageError errorInfo={RECAPTCHA_ERROR_INFO} onClose={() => setRecaptchaError(false)} />
                  )}
                </div>
                <div className="btn-sign-up-container">
                  <ButtonCustom
                    name="btn-sign-up"
                    type="submit"
                    text={l('signUp.register.btn')}
                    textLoading={l('form.sending')}
                    loading={isSubmitting || loading || legalDocumentsLoading}
                    disabled={hasValidationErrors || !legalDocumentsReady || legalDocumentsLoading}
                    size="full-square"
                    variant="contained"
                    onClick={() => {
                      if (!_.isEmpty(errors)) {
                        if (errors.email) {
                          scrollIntoView(emailRef);
                        } else if (errors.password) {
                          scrollIntoView(passwordRef);
                        } else if (errors.name) {
                          scrollIntoView(nameRef);
                        } else if (errors.termsAndConditions) {
                          scrollIntoView(termsAndConditionsRef);
                        }
                      }
                    }}
                  />
                </div>
                <div className="link-login">
                  <span>{l('signUp.alreadyHaveAccount')} </span>
                  <ButtonLink
                    text={l('login.btn')}
                    onClick={() => history.push(`${Routes.LOGIN}${window.document.location.search}`)}
                  />
                </div>
              </div>
              <ReCaptcha sitekey={KEY_RECAPTCHA} action={ACTION} />
            </form>
          );
        }}
      </Formik>
      <Dialog
        open={showTermsAndConditions}
        type="md"
        dividers={false}
        unpaddingContent
        fullScreenMobile
        titleCenter
        paperClassName={LEGAL_DOCUMENT_DIALOG_PAPER_CLASS}
        handleClose={() => setShowTermsAndConditions(false)}
        body={
          <LegalDocumentViewer
            documentType="TERMS_AND_CONDITIONS"
            country={selectedCountry}
            language={language}
            fallback={<TermsAndConditions />}
          />
        }
      />
      <Dialog
        open={showPrivacyPolicies}
        type="md"
        dividers={false}
        unpaddingContent
        fullScreenMobile
        titleCenter
        paperClassName={LEGAL_DOCUMENT_DIALOG_PAPER_CLASS}
        handleClose={() => setShowPrivacyPolicies(false)}
        body={
          <LegalDocumentViewer
            documentType="PRIVACY_POLICY"
            country={selectedCountry}
            language={language}
            fallback={<PrivacyPolicies />}
          />
        }
      />
    </div>
  );
};

SignUpForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  errorInfo: PropTypes.object,
  onSubmit: PropTypes.func,
  isSandbox: PropTypes.bool,
};

export default SignUpForm;
